import { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Add } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '../../components/PageHeader'
import { useSensorDefinitions, useCreateSensorDefinition } from '../../hooks/useDevices'
import { format } from 'date-fns'

const DATA_TYPES = ['number', 'string', 'boolean']

const emptyForm = { key: '', name: '', unit: '', data_type: 'number' }

export default function SensorDefinitionsPage() {
  const { t } = useTranslation()
  const { data: defs = [], isLoading } = useSensorDefinitions()
  const createDef = useCreateSensorDefinition()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)

  const handleSubmit = async () => {
    await createDef.mutateAsync(form)
    setOpen(false)
    setForm(emptyForm)
  }

  const columns: GridColDef[] = [
    { field: 'key', headerName: t('sensors.key'), width: 160, renderCell: ({ value }) => (
      <Chip label={value} size="small" sx={{ fontFamily: 'monospace' }} />
    )},
    { field: 'name', headerName: t('sensors.displayName'), flex: 1, minWidth: 160,
      renderCell: ({ row }) => {
        const i18nKey = `sensorNames.${row.key}`
        const translated = t(i18nKey, { defaultValue: '' })
        return translated && translated !== i18nKey ? translated : row.name
      },
    },
    { field: 'unit', headerName: t('sensors.unit'), width: 120 },
    {
      field: 'data_type',
      headerName: t('sensors.dataType'),
      width: 120,
      renderCell: ({ value }) => (
        <Chip
          label={value}
          size="small"
          color={value === 'number' ? 'primary' : value === 'boolean' ? 'secondary' : 'default'}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: t('device.created'),
      width: 150,
      valueFormatter: (v: string) => (v ? format(new Date(v), 'PP') : '—'),
    },
  ]

  return (
    <Box>
      <PageHeader
        title={t('sensors.title')}
        subtitle={t('sensors.subtitle')}
        breadcrumbs={[{ label: t('nav.devices') }, { label: t('sensors.title') }]}
        action={
          <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>
            {t('sensors.createSensor')}
          </Button>
        }
      />

      <DataGrid
        rows={defs}
        columns={columns}
        loading={isLoading}
        autoHeight
        pageSizeOptions={[10, 25]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('sensors.createSensor')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label={t('sensors.keyLabel')}
            value={form.key}
            onChange={(e) => setForm((p) => ({ ...p, key: e.target.value.toLowerCase() }))}
            required fullWidth autoFocus
            helperText={t('sensors.keyHelperText')}
          />
          <TextField
            label={t('sensors.displayName')}
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            required fullWidth
          />
          <TextField
            label={t('sensors.unitLabel')}
            value={form.unit}
            onChange={(e) => setForm((p) => ({ ...p, unit: e.target.value }))}
            fullWidth
          />
          <TextField
            select label={t('sensors.dataType')}
            value={form.data_type}
            onChange={(e) => setForm((p) => ({ ...p, data_type: e.target.value }))}
            fullWidth
          >
            {DATA_TYPES.map((dt) => (
              <MenuItem key={dt} value={dt}>{dt}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createDef.isPending || !form.key || !form.name}
          >
            {t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
